import {useTranslation} from 'react-i18next';
import {Globe} from 'lucide-react';
import React from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Icon, Row, Text} from '@/ui';

interface LanguageSwitchProps {
 variant?: 'compact' | 'full';
}

export const LanguageSwitch = React.memo(function LanguageSwitch({variant = 'full'}: LanguageSwitchProps): React.ReactElement {
 const {i18n} = useTranslation();
 const [isOpen, setIsOpen] = useState(false);
 const dropdownRef = useRef<HTMLDivElement>(null);

 const languages = [
 {code: 'sv', label: 'Svenska', flag: '🇸🇪'},
 {code: 'en', label: 'English', flag: '🇬🇧'},
 ];

 const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]!;

 const changeLanguage = (langCode: string): void => {
 void i18n.changeLanguage(langCode);
 setIsOpen(false);
 };

 // Close dropdown when clicking outside
 useEffect(() => {
 const handleClickOutside = (event: MouseEvent): void => {
 if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
 setIsOpen(false);
 }
 };

 if (isOpen) {
 document.addEventListener('mousedown', handleClickOutside);
 }

 return () => {
 document.removeEventListener('mousedown', handleClickOutside);
 };
 }, [isOpen]);

  if (variant === 'compact') {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Change language"
          >
            <Icon icon={Globe} size="md" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={i18n.language === lang.code ? 'bg-foreground/[0.04] font-medium' : ''}
            >
              <Row align="center" gap={2}>
                <Text>{lang.flag}</Text>
                <Text size="sm">{lang.label}</Text>
              </Row>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

 return (
   <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
     <DropdownMenuTrigger asChild>
       <Button
         variant="ghost"
         aria-label="Change language"
       >
         <Row align="center" gap={2}>
           <Icon icon={Globe} size="md" />
           <Text>{currentLanguage.flag}</Text>
           <Text size="body-sm-responsive" variant="secondary" className="hidden md:inline">
             {currentLanguage.label}
           </Text>
         </Row>
       </Button>
     </DropdownMenuTrigger>
     <DropdownMenuContent align="end" className="w-48">
       {languages.map((lang) => (
         <DropdownMenuItem
           key={lang.code}
           onClick={() => changeLanguage(lang.code)}
           className={i18n.language === lang.code ? 'bg-muted font-medium' : ''}
         >
           <Row align="center" gap={2}>
             <Text>{lang.flag}</Text>
             <Text size="body-sm-responsive">{lang.label}</Text>
           </Row>
         </DropdownMenuItem>
       ))}
     </DropdownMenuContent>
   </DropdownMenu>
 );
});
